import { observer } from 'mobx-react'
import React from 'react'
import styles from './OCRScopeJobDetailBodyBasicInformation.sass'
import OCRScopingJobMainPanelViewModel from '../../OCRScopingJobMainPanelViewModel'
import OCRScopeJobDetailBodyTitle from '../OCRScopeJobDetailBodyTitle'
import Step from '../../../../../../creation/views/JobCreation/JobCreationFormView/Step'
import CompactWorkspaceSelector from '../../../../../../creation/views/JobCreation/JobCreationFormView/CompactWorkspaceSelector'
import CompactLegendSelector from '../../../../../../creation/views/JobCreation/JobCreationFormView/CompactLegendSelector'
import CompactOwnerSelector from '../../../../../../creation/views/JobCreation/JobCreationFormView/CompactOwnerSelector'
import CompactTeamSelector from '../../../../../../creation/views/JobCreation/JobCreationFormView/CompactTeamSelector'
import CompactRequestProviderSelector from '../../../../../../creation/views/JobCreation/JobCreationFormView/CompactRequestProviderSelector'
import {
  FormGroupContainer,
  FormRow,
} from '../../../../../../../components/inputs'
import {
  CustomSelect,
  useCustomSelectStateFromItemSource,
  ItemSource,
} from '../../../../../../../components/inputs/CustomSelect'
import { IconButton } from '../../../../../../../components/IconButton'
import Icon from '../../../../../../../components/Icon'
import { formatToLongDate } from '../../../../../../../utils/date-util'
import CompactSupportingClientSelector from '../../../../../../creation/views/JobCreation/JobCreationFormView/CompactSupportingClientSelector'

interface OCRScopeJobDetailsProps {
  vm: OCRScopingJobMainPanelViewModel
}
export default observer(function OCRScopeJobDetailBodyBasicInformation({
  vm,
}: OCRScopeJobDetailsProps) {
  const templateSelectProps = useCustomSelectStateFromItemSource({
    itemSource: React.useMemo(
      () =>
        ItemSource.static([vm.legendTemplate!], (item, text) => {
          const lowerCaseText = text.toLowerCase()
          return item.name.toLowerCase().includes(lowerCaseText)
        }),
      [vm.legendTemplate]
    ),
  })

  const workspaceSelector = vm.editor.workspaceSelector
  const workspaceSelectorStep = workspaceSelector.step

  const legendSelector = vm.editor.legendSelector
  const legendSelectorStep = legendSelector.step

  const ownerSelector = vm.editor.ownerSelector
  const ownerSelectorStep = ownerSelector.step

  const teamSelector = vm.editor.teamSelector
  const teamSelectorStep = teamSelector.step

  const supportingClientSelector = vm.editor.supportingClientSelector
  const supportingClientSelectorStep = supportingClientSelector.step

  const requestProviderSelector = vm.editor.requestProviderSelector
  const requestProviderSelectorStep = requestProviderSelector.step

  return (
    <div className={styles.root}>
      <OCRScopeJobDetailBodyTitle title={'Basic Information'} />
      <OCRScopeJobDetailBodyBasicInformationDocument vm={vm} />
      <div className={styles.line}></div>

      <div className={styles.content}>
        <Step
          title={'Workspace'}
          isLast={workspaceSelectorStep.position.last}
          descriptionHtml={workspaceSelectorStep.descriptionHtml}
          active={workspaceSelectorStep.isActive}
          becomeActive$={workspaceSelectorStep.becomeActive$}
          onBecomeVisible={workspaceSelectorStep.becomeVisible}
          onBecomeHidden={workspaceSelectorStep.becomeHidden}
          data-test={`step-${workspaceSelectorStep.stepId}`}
          compact
        >
          <CompactWorkspaceSelector vm={workspaceSelector} />
        </Step>
        <Step
          title={'Legend'}
          isLast={legendSelectorStep.position.last}
          descriptionHtml={legendSelectorStep.descriptionHtml}
          active={legendSelectorStep.isActive}
          becomeActive$={legendSelectorStep.becomeActive$}
          onBecomeVisible={legendSelectorStep.becomeVisible}
          onBecomeHidden={legendSelectorStep.becomeHidden}
          data-test={`step-${legendSelectorStep.stepId}`}
          compact
        >
          <CompactLegendSelector vm={legendSelector} disabled />
        </Step>
        {vm.legendTemplate && (
          <FormRow>
            <FormGroupContainer label="Template">
              <CustomSelect
                {...templateSelectProps}
                disabled
                placeholder="Select a template"
                value={vm.legendTemplate}
                multi={false}
                itemToString={(item) => item.name}
                onChange={(v) => vm.setLegendTemplate(v)}
              />
            </FormGroupContainer>
          </FormRow>
        )}
        <Step
          title={'Champion client'}
          isLast={ownerSelectorStep.position.last}
          descriptionHtml={ownerSelectorStep.descriptionHtml}
          active={ownerSelectorStep.isActive}
          becomeActive$={ownerSelectorStep.becomeActive$}
          onBecomeVisible={ownerSelectorStep.becomeVisible}
          onBecomeHidden={ownerSelectorStep.becomeHidden}
          data-test={`step-${ownerSelectorStep.stepId}`}
          compact
        >
          <CompactOwnerSelector vm={ownerSelector} />
        </Step>
        <Step
          title={'Team'}
          isLast={teamSelectorStep.position.last}
          descriptionHtml={teamSelectorStep.descriptionHtml}
          active={teamSelectorStep.isActive}
          becomeActive$={teamSelectorStep.becomeActive$}
          onBecomeVisible={teamSelectorStep.becomeVisible}
          onBecomeHidden={teamSelectorStep.becomeHidden}
          data-test={`step-${teamSelectorStep.stepId}`}
          compact
        >
          <CompactTeamSelector vm={teamSelector} />
        </Step>
        <Step
          title={'Supporting clients'}
          isLast={supportingClientSelectorStep.position.last}
          descriptionHtml={supportingClientSelectorStep.descriptionHtml}
          active={supportingClientSelectorStep.isActive}
          becomeActive$={supportingClientSelectorStep.becomeActive$}
          onBecomeVisible={supportingClientSelectorStep.becomeVisible}
          onBecomeHidden={supportingClientSelectorStep.becomeHidden}
          data-test={`step-${supportingClientSelectorStep.stepId}`}
          compact
        >
          <CompactSupportingClientSelector vm={supportingClientSelector} />
        </Step>
        {vm.editor.canEditDraftJobsAsAdmin && (
          <Step
            title={'Request provider'}
            isLast={requestProviderSelectorStep.position.last}
            descriptionHtml={requestProviderSelectorStep.descriptionHtml}
            active={requestProviderSelectorStep.isActive}
            becomeActive$={requestProviderSelectorStep.becomeActive$}
            onBecomeVisible={requestProviderSelectorStep.becomeVisible}
            onBecomeHidden={requestProviderSelectorStep.becomeHidden}
            data-test={`step-${requestProviderSelectorStep.stepId}`}
            compact
          >
            <CompactRequestProviderSelector vm={requestProviderSelector} />
          </Step>
        )}
      </div>
    </div>
  )
})

interface OCRScopeJobDetailsDocumentProps {
  vm: OCRScopingJobMainPanelViewModel
}

const OCRScopeJobDetailBodyBasicInformationDocument = observer(
  function OCRScopeJobDetailBodyBasicInformationDocument({
    vm,
  }: OCRScopeJobDetailsDocumentProps) {
    if (!vm.document) {
      return
    }

    return (
      <div className={styles.documentContainer}>
        <div className={styles.documentTitleContainer}>
          <div className={styles.documentTitle}>{vm.document.title}</div>
          <div className={styles.documentSubtitle}>
            {formatToLongDate(vm.document.dateCreated)}
          </div>
        </div>
        <IconButton onClick={vm.downloadDocument}>
          <Icon name="download" size={24} />
        </IconButton>
      </div>
    )
  }
)
